import { z } from 'zod';
import {
  leadTargetFiltersSchema,
  outboundChannelSchema,
  orgRecipientScopeSchema,
  orgTargetFiltersSchema,
  targetCommonFiltersSchema,
  targetQuerySchema,
  userTargetFiltersSchema,
  type OutboundChannel,
  type TargetPerson,
  type TargetSkipReason,
} from '@hero/schema';
import { API } from '..';

/**
 * Targeting — **who** gets a message, as one reusable query.
 *
 * Campaigns, the agent, bulk waybill sends, or any future "message these
 * people" feature call the same endpoint / service. Sources are unioned,
 * filters inside a source AND together, `common` applies to the union,
 * and the server always applies its own exclusions (opt-out, DND, dedupe
 * window, staff-held threads, unreachable on the requested channels).
 * Every field is `.describe()`d so the agent can compose the query too.
 * See `docs/ai/human-takeover.md` § 8.
 */
export const leadTargetFiltersAPISchema = leadTargetFiltersSchema.extend({
  business: leadTargetFiltersSchema.shape.business.describe(
    "Which brand's funnel to read (`Lead.funnels[].business`). Defaults to `hero`.",
  ),
  funnelStepCompleted: leadTargetFiltersSchema.shape.funnelStepCompleted.describe(
    'Leads that have completed ALL of these funnel steps (e.g. `["account_created"]`).',
  ),
  funnelStepNotCompleted: leadTargetFiltersSchema.shape.funnelStepNotCompleted.describe(
    'Leads that have completed NONE of these steps (e.g. `["first_waybill"]` = signed up, never issued).',
  ),
  leadStatusIn: leadTargetFiltersSchema.shape.leadStatusIn.describe(
    'Lead pipeline status is any of these (`new`, `contacted`, `engaged`, `qualified`, `activated`, `won` …).',
  ),
  hasOrders: leadTargetFiltersSchema.shape.hasOrders.describe(
    'true = placed an order / issued a waybill (funnel `first_order` / `first_waybill`); false = never has.',
  ),
  hasAccount: leadTargetFiltersSchema.shape.hasAccount.describe(
    'true = an account is behind the lead (`Lead.converted` or funnel `account_created`); false = still a guest.',
  ),
  tags: leadTargetFiltersSchema.shape.tags.describe('Any of these `Lead.UTags[].code` values.'),
  leadIds: leadTargetFiltersSchema.shape.leadIds.describe(
    'Hand-picked leads (the Leads list multi-select). Combined with every other filter.',
  ),
});

export const orgTargetFiltersAPISchema = orgTargetFiltersSchema.extend({
  orgIds: orgTargetFiltersSchema.shape.orgIds.describe('Hand-picked orgs.'),
  roles: orgTargetFiltersSchema.shape.roles.describe(
    'Orgs with ANY of these network roles on `Org.roles[]` — `carrier_company`, `broker`, `business_customer`, `individual_customer`.',
  ),
  status: orgTargetFiltersSchema.shape.status.describe('Org status is any of these. Defaults to `active`.'),
  createdDateType: orgTargetFiltersSchema.shape.createdDateType.describe(
    "Orgs created inside this window ('this_week', 'last_30_days' …).",
  ),
});

export const orgRecipientScopeAPISchema = orgRecipientScopeSchema.extend({
  memberTypes: orgRecipientScopeSchema.shape.memberTypes.describe(
    'Which members of each targeted org receive it — `admin` (default), `dispatcher`, `employee`, `driver` (drivers who have the app).',
  ),
  includeDrivers: orgRecipientScopeSchema.shape.includeDrivers.describe(
    'Also the `Driver` rows the org created for people WITHOUT the app — reachable on WhatsApp only. Default false.',
  ),
});

export const userTargetFiltersAPISchema = userTargetFiltersSchema.extend({
  userIds: userTargetFiltersSchema.shape.userIds.describe('Hand-picked accounts.'),
  phones: userTargetFiltersSchema.shape.phones.describe(
    'Accounts with any of these phones (E.164; `+`, spaces and dashes tolerated).',
  ),
  roles: userTargetFiltersSchema.shape.roles.describe('Accounts with ANY of these network roles on `User.roles[]`.'),
  status: userTargetFiltersSchema.shape.status.describe('Account status is any of these. Defaults to `active`.'),
  createdDateType: userTargetFiltersSchema.shape.createdDateType.describe(
    'Accounts created inside this window.',
  ),
});

export const targetCommonFiltersAPISchema = targetCommonFiltersSchema.extend({
  language: targetCommonFiltersSchema.shape.language.describe(
    "The person's follow-up / thread language ('ar' / 'en' / 'ur').",
  ),
  lastInboundDateType: targetCommonFiltersSchema.shape.lastInboundDateType.describe(
    "People who wrote to us on WhatsApp inside this window ('today', 'this_week', 'last_month' …).",
  ),
  lastInboundBefore: targetCommonFiltersSchema.shape.lastInboundBefore.describe(
    'People whose LAST WhatsApp message to us is older than this instant — "gone quiet since".',
  ),
  channelAccountId: targetCommonFiltersSchema.shape.channelAccountId.describe(
    'Only threads on this Hero-owned line count for the inbound window and the staff-held check.',
  ),
  excludePhones: targetCommonFiltersSchema.shape.excludePhones.describe('Never these phones.'),
  dedupeWindowDays: targetCommonFiltersSchema.shape.dedupeWindowDays.describe(
    'Skip anyone who already received THIS template on WhatsApp within this many days. Default 7; 0 disables.',
  ),
});

/** The query as accepted over HTTP — same shape as `targetQuerySchema`, with descriptions. */
export const targetQueryAPISchema = targetQuerySchema.extend({
  common: targetCommonFiltersAPISchema.default({}),
});

/**
 * `POST /targeting/resolve` — run a query, get the count, a sample, the
 * exclusions by reason and per-channel reach. Nothing is sent.
 *
 * `templateId` + `channels` make the exclusions channel-aware (dedupe
 * window is per template; `no_phone` only matters if WhatsApp is
 * requested). `limit` / `skip` page through the full resolved list.
 */
export const targetingResolveAPISchema = z.object({
  query: targetQueryAPISchema,
  templateId: z
    .string()
    .optional()
    .describe('Catalog template id — enables the per-template dedupe window.'),
  channels: z
    .array(outboundChannelSchema)
    .optional()
    .describe(
      'Channels the caller intends to send on. A person unreachable on ALL of them is excluded. Defaults to `["whatsapp"]`.',
    ),
  limit: z.coerce.number().int().min(0).max(500).default(0).describe('Page size for `targets[]`; 0 = sample only.'),
  skip: z.coerce.number().int().min(0).default(0),
});

export interface TargetingResolveResult {
  /** People that would be sent to (no `skip`). */
  count: number;
  /** People the sources matched before exclusions. */
  resolved: number;
  excluded: Record<TargetSkipReason, number>;
  /** How many sendable people each requested channel can reach. */
  byChannel: Partial<Record<OutboundChannel, number>>;
  /** Up to 20 sendable rows, newest activity first. */
  sample: TargetPerson[];
  /** The page requested with `limit` / `skip` (sendable rows only). */
  targets?: TargetPerson[];
  /**
   * Uploaded contacts only — what each valid row matched, before
   * exclusions. `invalid` counts rows whose phone did not parse.
   */
  matches?: TargetingContactMatches;
  /** Uploaded contacts only — sheet rows (1-based, header excluded) whose phone did not parse. */
  invalidRows?: Array<{ row: number; phone: string }>;
}

export interface TargetingContactMatches {
  lead: number;
  user: number;
  new: number;
  invalid: number;
}

/** Request is the *input* shape — `limit` / `skip` are optional for callers, defaulted by the pipe. */
export type TargetingResolveAPI = API<
  z.input<typeof targetingResolveAPISchema>,
  TargetingResolveResult
>;
